/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DnsModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { DnsRule } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { withEditChildMessageItemMixin }
    from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';
import { withFullModalMixin } from 'ajs/js/utilities/mixins';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { DnsClientIpMatchConfigItem }
    from './matches/dns-client-ip-match.config-item.factory';
import { DnsClientPortMatchConfigItem }
    from './matches/dns-client-port-match.config-item.factory';
import { DnsGeoLocationMatchConfigItem }
    from './matches/dns-geo-location-match.config-item.factory';
import { DnsTransportProtocolMatchConfigItem }
    from './matches/dns-transport-protocol-match.config-item.factory';
import { DnsQueryNameMatchConfigItem }
    from './matches/dns-query-name-match.config-item.factory';
import { DnsQueryTypeMatchConfigItem }
    from './matches/dns-query-type-match.config-item.factory';
import { DnsRuleActionConfigItem } from './dns-rule-action.config-item.factory';
import { IDnsRuleConfig } from './dns-policy.item.types';

import * as l10n from '../dns.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description DnsRule ConfigItem class.
 *
 * @author Abhinesh Gour
 */
export class DnsRuleConfigItem extends
    withEditChildMessageItemMixin<IDnsRuleConfig, typeof MessageItem>(
        withFullModalMixin(MessageItem),
    )<IDnsRuleConfig> {
    public static ajsDependencies = [
        'l10nService',
        'schemaService',
    ];

    /** Overriding getConfig()'s return type */
    public getConfig: () => IDnsRuleConfig;

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: DnsRule,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Method used to import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            DnsRuleModalComponent,
        } = await import(
            /* webpackChunkName: "dns-policy-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/dns-policy-modal/components/dns-rule-modal/dns-rule-modal.component'
        );

        return DnsRuleModalComponent as Type<Component>;
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.dnsRuleModalBreadcrumbTitle);
    }

    /**
     * We want to remove 'match' from the default configuration so that the user can add matches
     * manually.
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IDnsRuleConfig> {
        const defaultValues = this.getAjsDependency_('defaultValues');
        const type = this.objectType.toLowerCase();
        const defaultConfig = defaultValues.getDefaultItemConfigByType(type) || {};

        return {
            ...defaultConfig,
            match: null,
            action: null,
        };
    }

    /**
     * Getter for action configItem.
     */
    public get action(): DnsRuleActionConfigItem {
        return this.config.action;
    }

    /**
     * Getter of Dns Client IP Match config item.
     */
    public get clientIpAddress(): DnsClientIpMatchConfigItem {
        const { config: { match: { config: matchConfig } } } = this;

        return matchConfig.client_ip_address;
    }

    /**
     * Getter of Dns Geo Location Match config item.
     */
    public get geoLocation(): DnsGeoLocationMatchConfigItem {
        const { config: { match: { config: matchConfig } } } = this;

        return matchConfig.geo_location;
    }

    /**
     * Getter of Dns Client Port Match config item.
     */
    public get clientPortNumbers(): DnsClientPortMatchConfigItem {
        const { config: { match: { config: matchConfig } } } = this;

        return matchConfig.client_port_numbers;
    }

    /**
     * Getter of Dns Transport Protocol Match config item.
     */
    public get protocol(): DnsTransportProtocolMatchConfigItem {
        const { config: { match: { config: matchConfig } } } = this;

        return matchConfig.protocol;
    }

    /**
     * Getter of Dns Query Name Match config item.
     */
    public get queryName(): DnsQueryNameMatchConfigItem {
        const { config: { match: { config: matchConfig } } } = this;

        return matchConfig.query_name;
    }

    /**
     * Getter of Dns Query Type Match config item.
     */
    public get queryType(): DnsQueryTypeMatchConfigItem {
        const { config: { match: { config: matchConfig } } } = this;

        return matchConfig.query_type;
    }
}
